import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteAuthenticated, getAuthenticated, putAuthenticated } from './api';
import {
  DocumentHubResponse,
  FolderTableRow,
  GetDocumentRequest,
  GetDocumentResponse,
  SaveDocumentRequest,
  Document,
  Folders,
  DocumentTableRow,
  DeleteDocumentRequest,
  PricingDashboardDocumentsResponse,
} from '../models/documentHub';
import { format } from 'date-fns';

export const useGetPresignedUrl = (document: GetDocumentRequest) => {
  const url = `document-hub/document?documentType=${document.type}&documentName=${document.name}`;
  const { isPending, error, data } = useQuery<GetDocumentResponse>({
    queryKey: ['pricing-dashboard', document],
    enabled: document.name === '' ? false : true,
    queryFn: async () => {
      return getAuthenticated<GetDocumentResponse>(url);
    },
  });

  return {
    loading: isPending,
    error,
    preSignedUrl: data?.preSignedUrl || undefined,
  };
};

export const useMutateUploadDocument = () => {
  const url = `document-hub/document`;
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['document-hub'],
    mutationFn: async (request: SaveDocumentRequest) => {
      const body = JSON.stringify(request);
      return putAuthenticated<SaveDocumentRequest>(url, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['document-hub'] });
    },
  });
};

export const useMutateDeleteDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['document-hub'],
    mutationFn: async (document: DeleteDocumentRequest) => {
      const url = `document-hub/document?documentType=${document.documentType}&documentName=${document.documentName}`;
      return deleteAuthenticated<DeleteDocumentRequest>(url);
    },
    onSuccess: async () => {
      //remove from cache
      await queryClient.invalidateQueries({ queryKey: ['document-hub'] });
    },
  });
};

export const useMutateGetDocument = () => {
  return useMutation({
    mutationKey: ['document-hub'],
    mutationFn: async (document: GetDocumentRequest) => {
      const url = `document-hub/document?documentType=${document.type}&documentName=${document.name}`;
      return getAuthenticated<GetDocumentResponse>(url);
    },
  });
};

export const useGetDocumentTree = () => {
  const url = `document-hub/document-tree`;
  const { isPending, error, data } = useQuery({
    queryKey: ['document-hub'],
    queryFn: async () => {
      const res = await getAuthenticated<DocumentHubResponse>(url);
      return buildTableData(res);
    },
  });

  return {
    loading: isPending,
    error,
    data: data || undefined,
  };
};

export const useGetPricingDashboardDocuments = () => {
  const url = `document-hub/pricing-dashboard`;
  const { isPending, error, data } = useQuery({
    queryKey: ['pricing-dashboard'],
    queryFn: async () => {
      return await getAuthenticated<PricingDashboardDocumentsResponse>(url);
    },
  });

  return {
    loading: isPending,
    error,
    data: data || undefined,
  };
};

const buildTableData = (data: DocumentHubResponse) => {
  return Object.entries(Folders).map(([key, value]) => {
    return createData(key, value, data.documentTree[key]);
  });
};

const createData = (
  folderKey: string,
  folderName: string,
  documents?: Document[]
): FolderTableRow => {
  const formattedDocs = documents
    ? createDocuments(folderKey, folderName, documents)
    : [];

  const latestDocument = documents ? getLatestDocument(documents) : undefined;

  const createdOn = documents
    ? formattedDate(getLatestDocument(documents).createdOn)
    : '---';

  return {
    folderKey,
    folderName,
    documents: formattedDocs,
    createdOn,
    createdBySub: latestDocument
      ? `${latestDocument.createdByFirstName} ${latestDocument.createdByLastName}`
      : '---',
  };
};

const formattedDate = (date: string) => {
  return format(new Date(date), 'MM/dd/yyyy - h:mm a');
};

const getLatestDocument = (documents: Document[]) => {
  return documents.reduce((prev, current) =>
    prev.createdOn > current.createdOn ? prev : current
  );
};

const createDocuments = (
  folderKey: string,
  folderName: string,
  documents: Document[]
): DocumentTableRow[] => {
  return documents?.map((document, index) => ({
    id: index,
    folderKey: folderKey,
    folderName: folderName,
    uploadedBy: `${document.createdByFirstName} ${document.createdByLastName}`,
    createdOn: formattedDate(document.createdOn),
    documentName: document.documentName,
    hasBeenViewed: document.hasBeenViewed,
    actionButton: '',
  }));
};
